import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  addEvent,
  createSession,
  deleteSession,
  getSessionEvents,
  getUserSessions,
  updateSession,
} from "../../services/liveTagService";

export const liveTagKeys = {
  all: ["liveTag"],
  sessions: () => ["liveTag", "sessions"],
  sessionEvents: (sessionId) => ["liveTag", "sessions", sessionId, "events"],
};

export function useLiveTagSessions(options = {}) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: liveTagKeys.sessions(),
    queryFn: () => getUserSessions(navigate),
    staleTime: 60 * 1000,
    ...options,
  });
}

export function useLiveTagSessionEvents(sessionId, options = {}) {
  const navigate = useNavigate();

  return useQuery({
    queryKey: liveTagKeys.sessionEvents(sessionId),
    queryFn: () => getSessionEvents(sessionId, navigate),
    enabled: !!sessionId,
    staleTime: 30 * 1000,
    ...options,
  });
}

export function useCreateLiveTagSession() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => createSession(payload, navigate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: liveTagKeys.sessions() });
    },
  });
}

export function useAddLiveTagEvent(sessionId) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => addEvent(sessionId, payload, navigate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: liveTagKeys.sessionEvents(sessionId) });
      queryClient.invalidateQueries({ queryKey: liveTagKeys.sessions() });
    },
  });
}

export function useUpdateLiveTagSession(sessionId) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => updateSession(sessionId, payload, navigate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: liveTagKeys.sessions() });
    },
  });
}

export function useDeleteLiveTagSession() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId) => deleteSession(sessionId, navigate),
    onMutate: async (sessionId) => {
      await queryClient.cancelQueries({ queryKey: liveTagKeys.sessions() });
      const previous = queryClient.getQueryData(liveTagKeys.sessions());
      queryClient.setQueryData(liveTagKeys.sessions(), (old) =>
        Array.isArray(old) ? old.filter((s) => s.id !== sessionId) : old
      );
      return { previous };
    },
    onError: (_err, _sessionId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(liveTagKeys.sessions(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: liveTagKeys.sessions() });
    },
  });
}

export default useLiveTagSessions;
